import { resolveBackofficeOrigin } from "@qubictron/url/client";
import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import axios, { AxiosError } from "axios";
import { creditDenialEvents, getCreditDenial } from "./creditDenial";

interface RetryConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

const CSRF_COOKIE_URL = "/sanctum/csrf-cookie";

/**
 * What every request to the back office needs to be recognised as coming from
 * a signed-in browser: the cross-subdomain session cookie, plus the XSRF
 * header Laravel's stateful API insists on for anything but a GET.
 *
 * Exported so other instances cannot quietly drift from it - one that sent the
 * cookie without the header would 419 on every signed-in request while still
 * working for guests, which is exactly the kind of bug that reaches production.
 */
export const sessionRequestConfig = {
  baseURL: resolveBackofficeOrigin(),
  withCredentials: true,
  withXSRFToken: true,

  xsrfCookieName: "XSRF-TOKEN",
  xsrfHeaderName: "X-XSRF-TOKEN",
} as const;

/**
 * Refreshes an expired CSRF token once and replays the request.
 */
export const authInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,

    async (err: AxiosError) => {
      const originalRequest: RetryConfig | undefined = err.config;
      const isCsrfError = err.response?.status === 419;

      // Retry once: flag travels with the config object through Axios internals
      if (isCsrfError && originalRequest && !originalRequest._retried) {
        originalRequest._retried = true;
        await axiosInstance.get(CSRF_COOKIE_URL, {
          withCredentials: true,
        });
        return axiosInstance(originalRequest);
      }

      return Promise.reject(err);
    }
  );
};

/**
 * Announces a 402 `insufficient_credits` so the app can offer to buy QCredits.
 *
 * The rejection still reaches the caller untouched: this only adds the prompt,
 * it does not decide what a failed request means for the code that made it.
 */
// A CSRF retry replays the request through this chain, so the same rejection
// can pass the interceptor twice; it is announced once.
const announced = new WeakSet<object>();

export const creditDenialInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (err: unknown) => {
      const denial = getCreditDenial(err);

      if (denial !== null && typeof err === "object" && err !== null && !announced.has(err)) {
        announced.add(err);
        creditDenialEvents.emit(denial);
      }

      return Promise.reject(err);
    }
  );
};

/**
 * Session-authenticated client for the back office.
 *
 * The widget used to talk to the gateway directly with a key baked into the
 * bundle; the current user, saved shipments and ocean search go through the
 * back office instead, which authenticates the browser by the cross-subdomain
 * Sanctum cookie.
 */
export const backofficeClient = axios.create({
  baseURL: resolveBackofficeOrigin(),
  withCredentials: true,
  withXSRFToken: true,

  xsrfCookieName: "XSRF-TOKEN",
  xsrfHeaderName: "X-XSRF-TOKEN",
});

authInterceptor(backofficeClient);
creditDenialInterceptor(backofficeClient);

// Unwraps the body so callers get the DTO itself, as the previous
// gateway-facing instances did.
backofficeClient.interceptors.response.use(
  (response) => response.data,
  (error: AxiosError) => Promise.reject(error)
);
